import { useEffect, useRef, useState } from "react";
import { Download, Printer, Instagram } from "lucide-react";

const RSVP_URL = "https://newphiladelphiabaptist.org/family-day";
const QR_URL = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
  RSVP_URL
)}&size=800x800&margin=0&format=png&bgcolor=ffffff&color=1a1a1a`;

// Load an image and resolve when ready (CORS-safe for canvas export).
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Render helper — draws the entire flyer onto a canvas.
// mode: "print" (8.5x11 @ 150dpi = 1275x1650) OR "square" (1080x1080)
async function renderFlyer(canvas, mode = "print") {
  const isPrint = mode === "print";
  const W = isPrint ? 1275 : 1080;
  const H = isPrint ? 1650 : 1080;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background — vibrant red-to-deep-red gradient
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#C8102E");
  bg.addColorStop(0.55, "#a80d26");
  bg.addColorStop(1, "#6b0817");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Gold radial glow
  const glow = ctx.createRadialGradient(W * 0.85, H * 0.15, 50, W * 0.85, H * 0.15, W * 0.7);
  glow.addColorStop(0, "rgba(212,175,55,0.55)");
  glow.addColorStop(1, "rgba(212,175,55,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  const glow2 = ctx.createRadialGradient(W * 0.1, H * 0.9, 40, W * 0.1, H * 0.9, W * 0.6);
  glow2.addColorStop(0, "rgba(255,255,255,0.18)");
  glow2.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = glow2;
  ctx.fillRect(0, 0, W, H);

  // Confetti dots
  const dots = [
    { x: W * 0.08, y: H * 0.05, r: 6, c: "#D4AF37" },
    { x: W * 0.92, y: H * 0.08, r: 4, c: "#ffffff" },
    { x: W * 0.15, y: H * 0.95, r: 5, c: "#D4AF37" },
    { x: W * 0.78, y: H * 0.92, r: 3.5, c: "#ffffff" },
    { x: W * 0.05, y: H * 0.55, r: 3, c: "#D4AF37" },
    { x: W * 0.95, y: H * 0.5, r: 5, c: "#D4AF37" },
  ];
  dots.forEach((d) => {
    ctx.fillStyle = d.c;
    ctx.beginPath();
    ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
    ctx.fill();
  });

  // Top eyebrow
  const cx = W / 2;
  ctx.textAlign = "center";
  ctx.fillStyle = "#D4AF37";
  ctx.font = `bold ${isPrint ? 22 : 20}px -apple-system, Helvetica, Arial, sans-serif`;
  ctx.fillText("NEW PHILADELPHIA · MISSIONARY BAPTIST", cx, isPrint ? 120 : 85);

  // "You're Invited" eyebrow
  ctx.font = `bold ${isPrint ? 18 : 16}px -apple-system, Helvetica, Arial, sans-serif`;
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fillText("· YOU'RE INVITED ·", cx, isPrint ? 160 : 120);

  // Main title
  ctx.fillStyle = "#ffffff";
  ctx.font = `900 ${isPrint ? 110 : 82}px Georgia, 'Times New Roman', serif`;
  ctx.fillText("Friends &", cx, isPrint ? 305 : 235);
  ctx.fillText("Family Day", cx, isPrint ? 425 : 325);

  // Divider dot pattern
  ctx.fillStyle = "#D4AF37";
  const dividerY = isPrint ? 480 : 370;
  for (let i = -2; i <= 2; i++) {
    ctx.beginPath();
    ctx.arc(cx + i * 24, dividerY, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  // Tagline
  ctx.fillStyle = "#D4AF37";
  ctx.font = `bold ${isPrint ? 20 : 17}px -apple-system, Helvetica, Arial, sans-serif`;
  ctx.fillText("FAITH · FELLOWSHIP · FAMILY · FUN", cx, isPrint ? 525 : 405);

  // Date / time card
  const cardY = isPrint ? 590 : 460;
  const cardW = isPrint ? 800 : 700;
  const cardH = isPrint ? 200 : 175;
  const cardX = (W - cardW) / 2;
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  roundRect(ctx, cardX, cardY, cardW, cardH, 20, true);
  ctx.strokeStyle = "rgba(212,175,55,0.5)";
  ctx.lineWidth = 2;
  roundRect(ctx, cardX, cardY, cardW, cardH, 20, false, true);

  ctx.fillStyle = "#D4AF37";
  ctx.font = `bold ${isPrint ? 22 : 18}px -apple-system, Helvetica, Arial, sans-serif`;
  ctx.fillText("SUNDAY", cx, cardY + (isPrint ? 55 : 45));
  ctx.fillStyle = "#ffffff";
  ctx.font = `900 ${isPrint ? 78 : 64}px Georgia, serif`;
  ctx.fillText("October 11, 2026", cx, cardY + (isPrint ? 128 : 108));
  ctx.fillStyle = "rgba(255,255,255,0.88)";
  ctx.font = `bold ${isPrint ? 24 : 20}px -apple-system, Helvetica, Arial, sans-serif`;
  ctx.fillText("11:00 AM Worship Service", cx, cardY + (isPrint ? 172 : 148));

  // Perks (two lines)
  const perksY = cardY + cardH + (isPrint ? 55 : 45);
  ctx.fillStyle = "#D4AF37";
  ctx.font = `bold ${isPrint ? 26 : 22}px -apple-system, Helvetica, Arial, sans-serif`;
  ctx.fillText("$100 CASH REWARD for Largest Family Group", cx, perksY);
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.font = `${isPrint ? 22 : 18}px -apple-system, Helvetica, Arial, sans-serif`;
  ctx.fillText(
    "Fellowship Meal · Door Prizes · Giveaways",
    cx,
    perksY + (isPrint ? 42 : 34)
  );

  // QR code + scan message
  try {
    const qr = await loadImage(QR_URL);
    const qrSize = isPrint ? 240 : 200;
    const qrY = perksY + (isPrint ? 90 : 74);
    const qrX = cx - qrSize / 2;
    // White backer for QR
    ctx.fillStyle = "#ffffff";
    roundRect(ctx, qrX - 12, qrY - 12, qrSize + 24, qrSize + 24, 16, true);
    ctx.drawImage(qr, qrX, qrY, qrSize, qrSize);

    ctx.fillStyle = "#D4AF37";
    ctx.font = `bold ${isPrint ? 20 : 17}px -apple-system, Helvetica, Arial, sans-serif`;
    ctx.fillText("SCAN TO RSVP", cx, qrY + qrSize + (isPrint ? 50 : 42));
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = `${isPrint ? 18 : 15}px -apple-system, Helvetica, Arial, sans-serif`;
    ctx.fillText(
      "newphiladelphiabaptist.org/family-day",
      cx,
      qrY + qrSize + (isPrint ? 80 : 68)
    );
  } catch (e) {
    // QR failed to load — fall back to URL only
    ctx.fillStyle = "#D4AF37";
    ctx.font = `bold ${isPrint ? 20 : 17}px -apple-system, Helvetica, Arial, sans-serif`;
    ctx.fillText("RSVP AT", cx, perksY + (isPrint ? 130 : 110));
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${isPrint ? 26 : 22}px -apple-system, Helvetica, Arial, sans-serif`;
    ctx.fillText(
      "newphiladelphiabaptist.org/family-day",
      cx,
      perksY + (isPrint ? 170 : 145)
    );
  }

  // Footer — address + phone
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.font = `bold ${isPrint ? 20 : 16}px -apple-system, Helvetica, Arial, sans-serif`;
  const footY = H - (isPrint ? 90 : 70);
  ctx.fillText("533 S. Mendenhall Rd. · Memphis, TN 38117", cx, footY);
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.font = `${isPrint ? 18 : 14}px -apple-system, Helvetica, Arial, sans-serif`;
  ctx.fillText("(901) 685-8877 · info@newphiladelphiabaptist.org", cx, footY + (isPrint ? 30 : 24));
}

// Helper — draws a rounded rectangle
function roundRect(ctx, x, y, w, h, r, fill, stroke) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  if (fill) ctx.fill();
  if (stroke) ctx.stroke();
}

export default function FamilyDayFlyer() {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState("print");
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    (async () => {
      if (!canvasRef.current) return;
      setRendering(true);
      try {
        await renderFlyer(canvasRef.current, mode);
      } finally {
        setRendering(false);
      }
    })();
  }, [mode]);

  const download = () => {
    if (!canvasRef.current) return;
    const link = document.createElement("a");
    link.download =
      mode === "print"
        ? "npmbc-family-day-flyer-print.png"
        : "npmbc-family-day-flyer-social.png";
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  };

  return (
    <div data-testid="family-day-flyer-generator">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-full bg-npbc-cream p-1">
          <button
            type="button"
            onClick={() => setMode("print")}
            data-testid="flyer-mode-print"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
              mode === "print"
                ? "bg-npbc-red text-white shadow"
                : "text-npbc-black hover:bg-npbc-black/5"
            }`}
          >
            <Printer className="h-3.5 w-3.5" />
            Print 8.5 × 11
          </button>
          <button
            type="button"
            onClick={() => setMode("square")}
            data-testid="flyer-mode-square"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
              mode === "square"
                ? "bg-npbc-red text-white shadow"
                : "text-npbc-black hover:bg-npbc-black/5"
            }`}
          >
            <Instagram className="h-3.5 w-3.5" />
            Social 1:1
          </button>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={rendering}
          data-testid="flyer-download-btn"
          className="ml-auto btn-primary !py-2 !px-4 text-xs disabled:opacity-60"
        >
          <Download className="h-3.5 w-3.5" />
          {rendering ? "Preparing..." : "Download PNG"}
        </button>
      </div>

      <div className="rounded-2xl overflow-hidden bg-white ring-1 ring-black/10 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)]">
        <canvas
          ref={canvasRef}
          data-testid="flyer-canvas"
          className="block w-full h-auto"
          aria-label="Downloadable flyer preview"
        />
      </div>
      <p className="mt-3 text-[11px] text-npbc-gray/70">
        Preview auto-updates when you switch formats. Click <strong>Download PNG</strong> to save a
        print-ready or social-ready flyer with the RSVP QR code baked in.
      </p>
    </div>
  );
}
